/* eslint-disable no-underscore-dangle */
import * as L from 'leaflet';
import 'leaflet-fullscreen';
import {
  LeafletDirective, LeafletDirectiveWrapper,
} from '@bluehalo/ngx-leaflet';

import Geocoder from 'leaflet-control-geocoder';
import type { OnDestroy, OnInit } from '@angular/core';
import { Directive, Inject, Input, NgZone } from '@angular/core';
import { GeocoderService } from '../geocoding/geocoder.service';

@Directive({
  selector: '[mtxGenericControlsLayer]',
  standalone: false,
})
export class GenericControlsLayerDirective implements OnInit, OnDestroy {

  @Input()
  public mtxGenericControlsLayer: any;

  private readonly leafletDirectiveWrapper: LeafletDirectiveWrapper;

  private centerControl?: L.Control;

  private fullscreenControl?: L.Control;

  private zoomControl?: L.Control.Zoom;

  private searchControl?: Geocoder;

  public constructor(@Inject(LeafletDirective) leafletDirective: LeafletDirective,
    @Inject(GeocoderService) private readonly geocodeService: GeocoderService,
    @Inject(NgZone) private readonly zone: NgZone) {
    this.leafletDirectiveWrapper = new LeafletDirectiveWrapper(leafletDirective);
  }

  public ngOnInit(): void {
    this.addControls();
  }

  public ngOnDestroy(): void {
    this.centerControl?.remove();
    this.zoomControl?.remove();
    this.searchControl?.remove();
    this.fullscreenControl?.remove();
  }

  private addControls(): void {
    this.zone.runOutsideAngular(() => {
      const map = this.leafletDirectiveWrapper.getMap();
      this.fullscreenControl = new (L as any).Control.Fullscreen({
        content: 'FS',
        position: 'topright',
      }).addTo(map);
      this.centerControl = this.buildCenterControl({
        position: 'topright',
      }).addTo(map);
      this.zoomControl = L.control.zoom({ position: 'bottomright' }).addTo(map);
      this.searchControl = new Geocoder({
        geocoder: this.geocodeService,
        expand: 'click',
        suggestTimeout: 200,
        queryMinLength: 1,
        position: 'topright',
      }).addTo(map);

      /*
       * With `expand: 'click'` the geocoder binds a click handler on its
       * container that toggles (and thus collapses) the search box. Leaflet's
       * disableClickPropagation on the input only suppresses mousedown/touch,
       * not click, so clicking into the textbox bubbles up and collapses it.
       * Stop the click here so placing the cursor keeps the box open.
       */
      const searchInput = (this.searchControl as any)._input as HTMLElement | undefined;
      if (searchInput) {
        L.DomEvent.addListener(searchInput, 'click', L.DomEvent.stopPropagation);
      }
      this.searchControl.on('markgeocode', e => {
        map.fire('geocodermarkgeocode', { latlng: e.geocode.center });
      });
    });
  }

  private buildCenterControl(options?: L.ControlOptions): L.Control {
    const centerControl = new L.Control(options ?? {});


    centerControl.onAdd = function(mapRef): HTMLElement {
      const self = this as any;
      self._map = mapRef;

      // <i class="ati-geo-fencing"></i>
      self._container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
      const linkClass =
        'leaflet-control-zoom-fullscreen ati-geo-fencing';
      const inner = L.DomUtil.create('a',
        linkClass,
        self._container as (HTMLElement | undefined));
      inner.href = '#';
      inner.title = 'Center All';
      inner.onclick = (evt): void => {
        evt.preventDefault();
        const allLayers: (L.Bounds | L.LatLng | L.LatLngBounds)[] = [];
        self._map.eachLayer((layer: L.Layer) => {
          if (layer instanceof L.Marker) {
            allLayers.push(layer.getLatLng());
          } else if (layer instanceof L.Polygon) {
            allLayers.push(layer.getBounds());
          } else if (layer instanceof L.Circle) {
            allLayers.push(layer.getBounds());
          } else if (layer instanceof L.Polyline) {
            allLayers.push(layer.getBounds());
          }
        });

        if (allLayers.length > 0) {
          self._map.fitBounds(allLayers);
        }
      };

      return self._container;
    };

    return centerControl;
  }

}
